import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, TextField, Popper } from '@mui/material';
import moment from 'moment';
import { Link } from 'react-router-dom';
import editIcon from '../../../../assets/allInvoice-assets/edit.svg';
import CheckIcon from '../../../../assets/allInvoice-assets/check.svg';
import CloseIcon from '../../../../assets/allInvoice-assets/x.svg';
import Action from './Action';
import Toast from '../../utils/Toast';
import CustomTooltip from '../../utils/CustomTooltip';
import { ChipStyled, classes, StyledBox } from './utils';
import { renameDocument } from '../../../../services/allInvoiceApi';
import { globalClasses } from '../../utils/globalUtils';
import { DOCUMENT_ACTION_TOOLTIP } from '../../../../constants/tooltipConstant';
import { lightPalette } from '../../../../theme';
import MODELS from '../../../../constants/models';
import { DOCUMENT_TOAST } from '../../../../constants/toastConstant';

const DocumentNameRow = (params) => {
  const id = params?.data?.id;
  const status = params?.data?.status;
  const fileName = params?.value;
  const documentType = params?.data?.document_type;

  const onClickEditInvoice = () => {
    params?.api.startEditingCell({
      rowIndex: params?.rowIndex,
      colKey: 'name',
    });
  };

  if (params?.value !== undefined) {
    let documentTypeString;
    switch (documentType) {
      case MODELS.INVOICE:
        documentTypeString = 'invoice';
        break;
      case MODELS.RECEIPT:
        documentTypeString = 'receipt';
        break;
      case MODELS.SALES:
        documentTypeString = 'sale';
        break;
      case MODELS.CREDIT_CARD_STATEMENT:
        documentTypeString = 'creditcardstatement';
        break;
      case MODELS.BANK_STATEMENT:
        documentTypeString = 'bank-statement';
        break;
      default:
        documentTypeString = 'unknown';
    }
    return (
      <Box className={classes.documentRowMainBox}>
        <Box className={classes.documentNameBox}>
          {status === 'Completed' ? (
            <CustomTooltip title={fileName}>
              <Link to={`/${documentTypeString}/${id}`} className="linkStyle">
                <Typography
                  variant="body1"
                  fontWeight="400"
                  className={classes.documentRowTypography}
                >
                  {fileName}
                </Typography>
              </Link>
            </CustomTooltip>
          ) : (
            <CustomTooltip title={fileName}>
              <Typography
                variant="body1"
                fontWeight="400"
                className={`${classes.documentRowTypography} ${classes.documentRowTypographyColor}`}
              >
                {fileName}
              </Typography>
            </CustomTooltip>
          )}
        </Box>
        <Box>
          <Box className={`my-icon ${classes.editIconBox}`}>
            <CustomTooltip title={DOCUMENT_ACTION_TOOLTIP.RENAME_DOCUMENT}>
              <Box
                component="img"
                src={editIcon}
                alt="Edit"
                className={classes.editIcon}
                onClick={onClickEditInvoice}
                style={{ height: '15px', width: '15px' }}
              />
            </CustomTooltip>
          </Box>
        </Box>
      </Box>
    );
  }
};

const CustomCellEditor = (params) => {
  const fileName = params?.value;
  const [currentValue, setCurrentValue] = useState('');
  const [extension, setExtension] = useState('');
  const documentId = params?.data?.id;

  // Extract name and extension on component mount
  useEffect(() => {
    const nameWithoutExtension = fileName.split('.').slice(0, -1).join('.');
    const fileExtension = fileName.split('.').pop();
    setExtension(fileExtension);
    setCurrentValue(nameWithoutExtension);
  }, [fileName]);

  const UpdateColumn = async (newName) => {
    try {
      // Assuming renameDocument expects the new name with extension
      if (/[ !@#$%^&*]/.test(newName)) {
        Toast({
          message: `"${newName}" ${DOCUMENT_TOAST.SPACES_OR_SPECIAL_CHARS_NOT_ALLOWED}`,
          variant: 'error',
          position: { vertical: 'top', horizontal: 'center' },
        });
        return;
      }
      const newFileName = `${newName}.${extension}`;
      const response = await renameDocument(documentId, newFileName);
      if (response) {
        params?.api.purgeInfiniteCache();
        Toast({
          message: response?.data?.message,
          variant: 'success',
        });
      }
      return response;
    } catch {
      params?.api.stopEditing();
    }
  };

  const handleRightOption = async () => {
    UpdateColumn(currentValue);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      setCurrentValue(event.target.value);
      UpdateColumn(event.target.value);
    }
  };

  const handleWrongOption = () => {
    params?.api.stopEditing();
  };

  const handleTextFieldFocus = () => {
    const textField = document.querySelector('.editCellTextfield');
    textField.addEventListener('keydown', handleKeyDown);
  };

  return (
    <Box className={classes.documentEditCellBox}>
      <TextField
        fullWidth
        value={currentValue}
        onChange={(event) => setCurrentValue(event.target.value)}
        className={classes.editCellTextfield}
        onFocus={handleTextFieldFocus}
      />
      <Box className={classes.editCellActionIcons}>
        <Box
          component="img"
          src={CheckIcon}
          alt="CheckIcon"
          className={`${classes.editIcon} ${
            fileName !== currentValue
              ? globalClasses.visible
              : globalClasses.notVisibility
          }`}
          onClick={handleRightOption}
        />
        <Box
          component="img"
          src={CloseIcon}
          alt="CloseIcon"
          className={classes.editIcon}
          onClick={handleWrongOption}
        />
      </Box>
    </Box>
  );
};

const StatusRow = (params) => {
  let statusValue;
  const value = params?.value;
  switch (value) {
    case 'Failed':
      statusValue = (
        <CustomTooltip title={params?.data?.reason}>
          <Chip
            size="small"
            label={params?.value}
            className={`${globalClasses.whiteTextColor} ${classes.failedChipColor}`}
          />
        </CustomTooltip>
      );
      break;
    case 'Pending':
      statusValue = (
        <Chip
          size="small"
          label={params?.value}
          className={`${globalClasses.whiteTextColor} ${classes.pendingChipColor}`}
        />
      );
      break;
    case 'Completed':
      statusValue = (
        <Chip
          size="small"
          label={params?.value}
          className={`${globalClasses.whiteTextColor} ${classes.completedChipColor}`}
        />
      );
      break;
    case 'Inprogress':
      statusValue = (
        <Chip
          size="small"
          label={params?.value}
          className={`${globalClasses.whiteTextColor} ${classes.inProgressChipColor}`}
        />
      );
      break;
    default:
      statusValue = params.value;
      break;
  }
  return statusValue;
};

const TagRow = (params) => {
  const values = params?.value;

  const maxVisibleChips = 2;

  const [anchorEl, setAnchorEl] = useState(null);

  const handleChipMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChipMouseLeave = () => {
    setAnchorEl(null);
  };

  const visibleChips =
    values !== undefined ? values.slice(0, maxVisibleChips) : [];
  const invisibleChips =
    values !== undefined ? values.slice(maxVisibleChips) : [];

  if (params?.data) {
    return (
      <Box>
        {values !== undefined && values.length > 0 ? (
          <Box onMouseLeave={handleChipMouseLeave}>
            {visibleChips.map((value) => (
              <CustomTooltip
                key={value.id}
                title={value.inputValue ? value.inputValue : value.name}
              >
                <ChipStyled
                  label={value.inputValue ? value.inputValue : value.name}
                />
              </CustomTooltip>
            ))}
            {values.length > maxVisibleChips && (
              <ChipStyled
                label={`+${values.length - maxVisibleChips} more`}
                variant="outlined"
                onMouseEnter={handleChipMouseEnter}
              />
            )}
            <Popper open={Boolean(anchorEl)} anchorEl={anchorEl}>
              <StyledBox>
                {invisibleChips.map((value) => (
                  <ChipStyled
                    key={value?.id}
                    label={value?.inputValue ? value?.inputValue : value?.name}
                  />
                ))}
              </StyledBox>
            </Popper>
          </Box>
        ) : (
          <Typography>-</Typography>
        )}
      </Box>
    );
  }
};

const date = (params) => {
  const { value } = params;
  const formattedDate = moment.utc(value).local().format('DD-MM-YYYY HH:mm:ss');

  if (params?.data) {
    return (
      <Box>
        {value !== undefined && value !== null ? (
          <Typography variant="body1" fontWeight="400">
            {formattedDate}
          </Typography>
        ) : (
          <Box className={classes.dateValueBox}>
            <Typography>-</Typography>
          </Box>
        )}
      </Box>
    );
  }
};

const UserName = (params) => {
  const FIRST_NAME = params?.data?.User?.first_name;
  const LAST_NAME = params?.data?.User?.last_name;

  if (params?.data) {
    return (
      <Typography
        variant="body1"
        color={lightPalette.lunarBase.main}
        fontWeight="400"
      >
        {FIRST_NAME && LAST_NAME ? `${FIRST_NAME} ${LAST_NAME}` : '-'}
      </Typography>
    );
  }
};

const UpdatedBy = (params) => {
  const FIRST_NAME = params?.data?.UpdatedUser?.first_name;
  const LAST_NAME = params?.data?.UpdatedUser?.last_name;

  if (params?.data) {
    return (
      <Typography
        variant="body1"
        color={lightPalette.lunarBase.main}
        fontWeight="400"
      >
        {FIRST_NAME && LAST_NAME ? `${FIRST_NAME} ${LAST_NAME}` : '-'}
      </Typography>
    );
  }
};

const ImportOption = (params) => {
  const importOption = params?.data?.document_summaries?.[0]?.import_option;
  const folder = params?.data?.document_summaries?.[0]?.location || 'Unknown';

  if (params?.data) {
    return (
      <Typography
        variant="body1"
        color={lightPalette.lunarBase.main}
        fontWeight="400"
      >
        {['OneDrive', 'SharePoint'].includes(importOption)
          ? `${importOption} - ${folder}`
          : importOption || '-'}
      </Typography>
    );
  }
};

const DocumentType = (params) => {
  const importType = params?.data?.document_summaries?.[0]?.import_type;
  const flowName =
    params?.data?.document_summaries?.[0]?.flow_builder?.name || 'Unknown';

  if (params?.data) {
    return (
      <CustomTooltip title={flowName}>
        <Typography
          variant="body1"
          color={lightPalette.lunarBase.main}
          fontWeight="400"
          className={classes.documentRowTypography}
        >
          {importType === 'Automated'
            ? `Flow - ${flowName}`
            : importType || '-'}
        </Typography>
      </CustomTooltip>
    );
  }
};

const PartyName = (params) => {
  if (params?.data) {
    return (
      <CustomTooltip title={params?.value}>
        <Typography
          variant="body1"
          color={lightPalette.lunarBase.main}
          fontWeight="400"
          className={classes.documentRowTypography}
        >
          {params?.value}
        </Typography>
      </CustomTooltip>
    );
  }
};

const IconRenderer = (params) => {
  const id = params?.data?.id;
  const status = params?.data?.status;
  const validatedAt = params?.data?.validate_at;
  const result = params?.data?.annotation_data
    ? params?.data?.annotation_data
    : params?.data?.mapped_result;
  const documentName = params?.data?.name;
  const [specificDocumentTag, setSpecificDocumentTag] = useState([]);
  const documentType = params?.data?.document_type;

  const handleApiCompletion = () => {
    params?.api.purgeInfiniteCache();
  };

  const handleTagApiCompletion = (updatedTagName) => {
    const node = params?.api.getRowNode(id);

    node.data.associatedTags = updatedTagName;

    node.updateData({ ...node.data });
  };

  const handleClick = () => {
    setSpecificDocumentTag(params?.data?.associatedTags);
  };

  return (
    <Box onClick={handleClick}>
      <Action
        id={id}
        documentName={documentName}
        documentTag={specificDocumentTag}
        onApiCompletion={handleApiCompletion}
        onTagApiCompletion={handleTagApiCompletion}
        result={result}
        status={status}
        validatedAt={validatedAt}
        documentType={documentType}
      />
    </Box>
  );
};

const VersionRow = (params) => {
  const id = params?.data?.id;
  const status = params?.data?.status;
  const documentType = params?.data?.document_type;
  let documentTypeString;
  switch (documentType) {
    case MODELS.INVOICE:
      documentTypeString = 'invoices';
      break;
    case MODELS.RECEIPT:
      documentTypeString = 'receipts';
      break;
    case MODELS.SALES:
      documentTypeString = 'sales';
      break;
    case MODELS.BANK_STATEMENT:
      documentTypeString = 'bank-statements';
      break;
    case MODELS.CREDIT_CARD_STATEMENT:
      documentTypeString = 'creditcardstatements';
      break;
    default:
      documentTypeString = 'unknown';
  }
  if (params?.data) {
    if (status === 'Completed') {
      return (
        <Link
          to={`/${documentTypeString}/versions/${id}`}
          className="linkStyle"
        >
          <Box
            className={`${globalClasses.displayFlex} ${globalClasses.displayAlignItem}`}
          >
            <Typography variant="body1">View Versions</Typography>
          </Box>
        </Link>
      );
    }
    return <Typography>-</Typography>;
  }
};

const documentColumnDef = (
  gridSettingData,
  gridKey,
  customFilterValue,
  screenWidth,
) => {
  const isSmallScreen = screenWidth < 600;

  return [
    {
      headerName: ' ',
      field: ' ',
      minWidth: isSmallScreen ? 50 : 70,
      maxWidth: isSmallScreen ? 50 : 70,
      headerComponent: 'HeaderCheckbox',
      checkboxSelection: true,
      sortable: false,
      resizable: false,
      pinned: isSmallScreen ? '' : 'left',
      lockPinned: true,
      lockPosition: true,
    },
    {
      headerName: 'Document Name',
      field: 'name',
      headerComponent: 'ResponsiveSortingHeader',
      headerComponentParams: {
        gridSettingData,
        gridKey,
      },
      minWidth: isSmallScreen ? 100 : 300,
      maxWidth: isSmallScreen ? 350 : 500,
      sortable: true,
      editable: true,
      cellRenderer: DocumentNameRow,
      cellEditor: CustomCellEditor,
      pinned: isSmallScreen ? '' : 'left',
      lockPinned: true,
      lockPosition: true,
      filter: true,
      suppressMenu: true,
      type: 'text',
      filterParams: {
        filterOptions: ['contains'],
        buttons: ['reset', 'apply'],
        suppressAndOrCondition: true,
        closeOnApply: true,
      },
      hide: false,
    },
    {
      headerName: 'Stage',
      field: 'status',
      headerComponent: 'ResponsiveSortingHeader',
      headerComponentParams: {
        gridSettingData,
        gridKey,
        customFilterValue,
      },
      minWidth: isSmallScreen ? 100 : 120,
      maxWidth: isSmallScreen ? 180 : 220,
      cellRenderer: StatusRow,
      sortable: true,
      // filter: 'CustomDropdownFilter',
      type: 'checkBox',
      filterParams: {
        // setCustomFilterValue,
        customFilterValue,
        gridKey,
      },
      hide: false,
    },
    {
      headerName: 'Uploaded By',
      field: 'fullName',
      headerComponent: 'ResponsiveSortingHeader',
      headerComponentParams: {
        gridSettingData,
        gridKey,
      },
      sortable: true,
      minWidth: isSmallScreen ? 100 : 150,
      maxWidth: isSmallScreen ? 150 : 200,
      cellRenderer: UserName,
      filter: true,
      suppressMenu: true,
      type: 'text',
      filterParams: {
        filterOptions: ['contains'],
        buttons: ['reset', 'apply'],
        suppressAndOrCondition: true,
        closeOnApply: true,
      },
    },
    {
      headerName: 'Import Option',
      field: 'document_summaries',
      headerComponent: 'ResponsiveSortingHeader',
      headerComponentParams: {
        gridSettingData,
        gridKey,
      },
      minWidth: isSmallScreen ? 100 : 150,
      maxWidth: isSmallScreen ? 150 : 200,
      cellRenderer: ImportOption,
    },
    {
      headerName: 'Type',
      field: 'type',
      headerComponent: 'ResponsiveSortingHeader',
      headerComponentParams: {
        gridSettingData,
        gridKey,
      },
      minWidth: isSmallScreen ? 100 : 150,
      maxWidth: isSmallScreen ? 150 : 200,
      cellRenderer: DocumentType,
    },
    {
      headerName: 'Updated By',
      field: 'fullName_updatedBy',
      headerComponent: 'ResponsiveSortingHeader',
      headerComponentParams: {
        gridSettingData,
        gridKey,
      },
      sortable: true,
      minWidth: isSmallScreen ? 100 : 150,
      maxWidth: isSmallScreen ? 150 : 200,
      cellRenderer: UpdatedBy,
      filter: true,
      suppressMenu: true,
      type: 'text',
      filterParams: {
        filterOptions: ['contains'],
        buttons: ['reset', 'apply'],
        suppressAndOrCondition: true,
        closeOnApply: true,
      },
    },
    {
      headerName: 'Uploaded At',
      field: 'created_at',
      minWidth: isSmallScreen ? 120 : 150,
      maxWidth: isSmallScreen ? 200 : 250,
      headerComponent: 'ResponsiveSortingHeader',
      headerComponentParams: {
        gridSettingData,
        gridKey,
      },
      filter: 'agDateColumnFilter',
      type: 'date',
      suppressMenu: true, // Hides the filter icon
      filterParams: {
        filterOptions: ['inRange'],
        buttons: ['reset', 'apply'],
        suppressAndOrCondition: true,
        closeOnApply: true,
        // minValidDate: formattedMinDate,
        // maxValidDate: todayString,
        comparator: (filterDate, cellValue) => {
          const cellDate = new Date(cellValue);
          if (cellDate < filterDate) return -1;
          if (cellDate > filterDate) return 1;
          return 0;
        },
      },
      sortable: true,
      cellRenderer: date,
    },
    {
      headerName: 'Validated At',
      field: 'validate_at',
      minWidth: 150,
      maxWidth: 250,
      headerComponent: 'ResponsiveSortingHeader',
      headerComponentParams: {
        gridSettingData,
        gridKey,
      },
      filter: 'agDateColumnFilter',
      type: 'date',
      suppressMenu: true, // Hides the filter icon
      filterParams: {
        filterOptions: ['inRange'],
        buttons: ['reset', 'apply'],
        suppressAndOrCondition: true,
        closeOnApply: true,
        // minValidDate: formattedMinDate,
        // maxValidDate: todayString,
        comparator: (filterDate, cellValue) => {
          const cellDate = new Date(cellValue);
          if (cellDate < filterDate) return -1;
          if (cellDate > filterDate) return 1;
          return 0;
        },
      },
      sortable: true,
      cellRenderer: date,
    },
    {
      headerName: 'Tags',
      field: 'associatedTags',
      headerComponent: 'ResponsiveSortingHeader',
      headerComponentParams: {
        gridSettingData,
        gridKey,
        customFilterValue,
      },
      minWidth: isSmallScreen ? 200 : 230,
      maxWidth: isSmallScreen ? 250 : 300,
      cellRenderer: TagRow,
      // filter: 'CustomDropdownFilter',
      type: 'checkBox',
      filterParams: {
        // setCustomFilterValue,
        customFilterValue,
        gridKey,
      },
    },
    {
      headerName: 'Version',
      field: 'versions',
      headerComponent: 'ResponsiveSortingHeader',
      headerComponentParams: {
        gridSettingData,
        gridKey,
      },
      minWidth: 120,
      maxWidth: 220,
      cellRenderer: VersionRow,
    },
    ...(gridKey === 'invoices'
      ? [
          {
            headerName: 'Invoice Date',
            field: 'invoice_date',
            minWidth: isSmallScreen ? 120 : 150,
            maxWidth: isSmallScreen ? 200 : 250,
            headerComponent: 'ResponsiveSortingHeader',
            headerComponentParams: {
              gridSettingData,
              gridKey,
            },
            filter: 'agDateColumnFilter',
            type: 'date',
            suppressMenu: true, // Hides the filter icon
            filterParams: {
              filterOptions: ['inRange'],
              buttons: ['reset', 'apply'],
              suppressAndOrCondition: true,
              closeOnApply: true,
              // minValidDate: formattedMinDate,
              // maxValidDate: todayString,
              comparator: (filterDate, cellValue) => {
                const cellDate = new Date(cellValue);
                if (cellDate < filterDate) return -1;
                if (cellDate > filterDate) return 1;
                return 0;
              },
            },
            sortable: true,
            cellRenderer: date,
          },
          {
            headerName: 'Party Name',
            field: 'party_name',
            headerComponent: 'ResponsiveSortingHeader',
            headerComponentParams: {
              gridSettingData,
              gridKey,
            },
            sortable: true,
            minWidth: 150,
            maxWidth: 200,
            cellRenderer: PartyName,
            filter: true,
            suppressMenu: true,
            type: 'text',
            filterParams: {
              filterOptions: ['contains'],
              buttons: ['reset', 'apply'],
              suppressAndOrCondition: true,
              closeOnApply: true,
            },
          },
          {
            headerName: 'Invoice No',
            field: 'invoice_number',
            headerComponent: 'ResponsiveSortingHeader',
            headerComponentParams: {
              gridSettingData,
              gridKey,
            },
            sortable: true,
            maxWidth: 150,
            minWidth: 200,
            cellRenderer: PartyName,
            filter: true,
            suppressMenu: true,
            type: 'text',
            filterParams: {
              filterOptions: ['contains'],
              buttons: ['reset', 'apply'],
              suppressAndOrCondition: true,
              closeOnApply: true,
            },
          },
        ]
      : []),
    {
      headerName: ' ',
      field: ' ',
      minWidth: 200,
      cellRenderer: IconRenderer,
    },
  ];
};
export default documentColumnDef;
